import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Ban, Camera, Check, Copy, ExternalLink, Hash, Link2, Loader2, Megaphone, Plus, Shield, Trash2, Volume2, X, Folder } from "lucide-react";

// Fenêtres de la console Discord (paramètres du serveur, rôles, profil du bot,
// salons, pseudo, expulsion, bannissement), au style de Discord.

const errorText = (err, fallback) => err?.response?.data?.detail || fallback;

export function Modal({ title, subtitle, onClose, children, footer, wide, testId }) {
  useEffect(() => {
    const key = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-[150] flex items-center justify-center bg-black/70 p-4" onMouseDown={onClose}>
      <div className={`flex max-h-[88vh] w-full ${wide ? "max-w-3xl" : "max-w-md"} flex-col overflow-hidden rounded-lg bg-[#313338] text-white shadow-2xl`}
        onMouseDown={(e) => e.stopPropagation()} data-testid={testId}>
        <div className="flex items-start gap-3 px-4 pb-2 pt-4">
          <div className="min-w-0 flex-1">
            <h2 className="text-xl font-semibold">{title}</h2>
            {subtitle && <p className="mt-1 text-sm text-white/60">{subtitle}</p>}
          </div>
          <button type="button" onClick={onClose} className="rounded p-1 text-white/50 hover:text-white" aria-label="Fermer"><X className="h-5 w-5" /></button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4">{children}</div>
        {footer && <div className="flex items-center justify-end gap-2 bg-[#2b2d31] px-4 py-3">{footer}</div>}
      </div>
    </div>
  );
}

const Btn = ({ children, onClick, kind = "primary", disabled, busy, testId, type = "button" }) => (
  <button type={type} onClick={onClick} disabled={disabled || busy} data-testid={testId}
    className={`inline-flex min-w-[96px] items-center justify-center gap-1.5 rounded px-4 py-2 text-sm font-medium transition disabled:opacity-50 ${
      kind === "danger" ? "bg-[#da373c] text-white hover:bg-[#a12828]"
        : kind === "ghost" ? "text-white hover:underline"
          : "bg-[#5865f2] text-white hover:bg-[#4752c4]"}`}>
    {busy && <Loader2 className="h-4 w-4 animate-spin" />}{children}
  </button>
);

const Label = ({ children }) => <div className="mb-1.5 mt-4 text-xs font-bold uppercase tracking-wide text-white/70">{children}</div>;
const inputCls = "w-full rounded bg-[#1e1f22] px-3 py-2 text-[15px] text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-[#5865f2]";

// Image choisie sur le PC (icône, avatar, bannière) : aperçu, retirer.
function ImagePick({ value, current, onChange, round = true, label, testId, wide }) {
  const ref = useRef(null);
  const shown = value === "" ? "" : value || current;
  const pick = (f) => {
    if (!f) return;
    if (!["image/png", "image/jpeg", "image/gif"].includes(f.type)) { toast.error("Image PNG, JPEG ou GIF"); return; }
    if (f.size > 4 * 1024 * 1024) { toast.error("Image trop lourde (4 Mo maximum)"); return; }
    const r = new FileReader();
    r.onload = () => onChange(r.result);
    r.readAsDataURL(f);
  };
  return (
    <div className="flex items-center gap-4">
      <button type="button" onClick={() => ref.current?.click()} title={label}
        className={`group relative flex shrink-0 items-center justify-center overflow-hidden bg-[#1e1f22] ${wide ? "h-20 w-40 rounded-lg" : `h-20 w-20 ${round ? "rounded-full" : "rounded-2xl"}`}`}>
        {shown ? <img src={shown} alt="" className="h-full w-full object-cover" /> : <Camera className="h-6 w-6 text-white/40" />}
        <span className="absolute inset-0 hidden items-center justify-center bg-black/60 text-[10px] font-bold uppercase group-hover:flex">Changer</span>
      </button>
      <div className="flex flex-col items-start gap-1.5">
        <input ref={ref} type="file" accept="image/png,image/jpeg,image/gif" className="hidden" data-testid={testId}
          onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ""; }} />
        <Btn onClick={() => ref.current?.click()}>{label}</Btn>
        {shown && <button type="button" onClick={() => onChange("")} className="text-xs text-white/60 hover:text-white hover:underline">Retirer</button>}
        <span className="text-[11px] text-white/40">PNG, JPEG ou GIF, 4 Mo max.</span>
      </div>
    </div>
  );
}

// ── Paramètres du serveur : vue d'ensemble, rôles, bannissements ──

export function ServerSettingsModal({ api, botId, guild, data, tab: startTab = "overview", onClose, onChanged }) {
  const [tab, setTab] = useState(startTab);
  const perms = data?.me?.perms || {};
  const tabs = [["overview", "Vue d'ensemble"], ["roles", "Rôles"], ["invites", "Invitations"], ["bans", "Bannissements"]];
  return (
    <div className="fixed inset-0 z-[150] flex bg-[#313338] text-white" data-testid="console-server-settings">
      <nav className="hidden w-[30%] min-w-[200px] shrink-0 justify-end bg-[#2b2d31] py-14 pr-2 sm:flex">
        <div className="w-48">
          <div className="truncate px-2.5 pb-1.5 text-xs font-bold uppercase text-white/50">{guild.name}</div>
          {tabs.map(([id, label]) => (
            <button key={id} type="button" onClick={() => setTab(id)} data-testid={`console-settings-tab-${id}`}
              className={`mb-0.5 block w-full rounded px-2.5 py-1.5 text-left text-[15px] ${tab === id ? "bg-white/10 text-white" : "text-white/60 hover:bg-white/5 hover:text-white"}`}>{label}</button>
          ))}
        </div>
      </nav>
      <main className="relative min-w-0 flex-1 overflow-y-auto px-5 py-14 sm:px-10">
        <div className="mb-4 flex gap-1 sm:hidden">
          {tabs.map(([id, label]) => (
            <button key={id} type="button" onClick={() => setTab(id)} className={`rounded px-2.5 py-1 text-sm ${tab === id ? "bg-white/10" : "text-white/60"}`}>{label}</button>
          ))}
        </div>
        <button type="button" onClick={onClose} className="fixed right-6 top-[96px] z-[1] flex flex-col items-center text-white/60 hover:text-white" aria-label="Fermer" data-testid="console-settings-close">
          <span className="flex h-9 w-9 items-center justify-center rounded-full border-2 border-current"><X className="h-5 w-5" /></span>
          <span className="mt-1 text-[11px] font-semibold">ÉCHAP</span>
        </button>
        <EscClose onClose={onClose} />
        <div className="max-w-2xl">
          {tab === "overview" && <Overview api={api} botId={botId} guild={guild} canEdit={perms.manage_guild} onChanged={onChanged} />}
          {tab === "roles" && <Roles api={api} botId={botId} guild={guild} data={data} onChanged={onChanged} />}
          {tab === "invites" && <Invites api={api} botId={botId} guild={guild} canManage={perms.manage_guild} />}
          {tab === "bans" && <Bans api={api} botId={botId} guild={guild} canBan={perms.ban} />}
        </div>
      </main>
    </div>
  );
}

function EscClose({ onClose }) {
  useEffect(() => {
    const key = (e) => { if (e.key === "Escape" && !document.querySelector("[data-testid='console-menu']")) onClose(); };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [onClose]);
  return null;
}

const NoPerm = ({ children }) => <div className="mb-4 rounded border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-100">{children}</div>;

function Overview({ api, botId, guild, canEdit, onChanged }) {
  const [name, setName] = useState(guild.name);
  const [icon, setIcon] = useState(null);
  const [busy, setBusy] = useState(false);
  const dirty = name.trim() !== guild.name || icon !== null;
  const save = async () => {
    setBusy(true);
    try {
      const body = {};
      if (name.trim() !== guild.name) body.name = name.trim();
      if (icon !== null) body.icon = icon;
      const r = await api.patch(`/${botId}/console/guilds/${guild.id}`, body);
      toast.success("Serveur mis à jour");
      setIcon(null);
      onChanged?.({ guilds: r.guilds });
    } catch (err) { toast.error(errorText(err, "Modification impossible")); }
    setBusy(false);
  };
  return (
    <>
      <h1 className="mb-5 text-xl font-semibold">Vue d'ensemble du serveur</h1>
      {!canEdit && <NoPerm>Le bot n'a pas la permission « Gérer le serveur » ici : Discord refusera les changements.</NoPerm>}
      <div className="flex flex-col gap-6 sm:flex-row">
        <ImagePick value={icon} current={guild.icon_url} onChange={setIcon} round label="Changer l'icône" testId="console-guild-icon" />
        <div className="flex-1">
          <Label>Nom du serveur</Label>
          <input value={name} onChange={(e) => setName(e.target.value)} maxLength={100} className={inputCls} data-testid="console-guild-name" />
        </div>
      </div>
      {dirty && (
        <div className="sticky bottom-4 mt-8 flex items-center gap-3 rounded-lg bg-[#111214] px-4 py-3 shadow-xl">
          <span className="flex-1 text-sm">Attention, il reste des modifications non enregistrées !</span>
          <Btn kind="ghost" onClick={() => { setName(guild.name); setIcon(null); }}>Réinitialiser</Btn>
          <Btn onClick={save} busy={busy} disabled={name.trim().length < 2} testId="console-guild-save">Enregistrer</Btn>
        </div>
      )}
    </>
  );
}

function Roles({ api, botId, guild, data, onChanged }) {
  const roles = Object.values(data?.roles || {}).sort((a, b) => b.position - a.position);
  const me = data?.me || {};
  const [sel, setSel] = useState(null);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const role = roles.find((r) => r.id === sel);
  useEffect(() => { if (role) setDraft({ name: role.name, color: role.color, hoist: role.hoist, mentionable: role.mentionable }); }, [sel]); // eslint-disable-line react-hooks/exhaustive-deps
  const editable = (r) => r && !r.managed && r.id !== guild.id && r.position < (me.top_position || 0) && me.perms?.manage_roles;
  const create = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/${botId}/console/guilds/${guild.id}/roles`, { name: "nouveau rôle" });
      await onChanged?.();
      if (r.role) setSel(r.role.id);
      toast.success("Rôle créé");
    } catch (err) { toast.error(errorText(err, "Création impossible")); }
    setBusy(false);
  };
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/${botId}/console/guilds/${guild.id}/roles/${role.id}`, draft);
      await onChanged?.();
      toast.success("Rôle enregistré");
    } catch (err) { toast.error(errorText(err, "Modification impossible")); }
    setBusy(false);
  };
  const remove = async () => {
    if (!window.confirm(`Supprimer le rôle « ${role.name} » ? Les membres le perdront.`)) return;
    setBusy(true);
    try {
      await api.del(`/${botId}/console/guilds/${guild.id}/roles/${role.id}`);
      setSel(null);
      await onChanged?.();
      toast.success("Rôle supprimé");
    } catch (err) { toast.error(errorText(err, "Suppression impossible")); }
    setBusy(false);
  };
  return (
    <>
      <div className="mb-5 flex items-center gap-3">
        <h1 className="flex-1 text-xl font-semibold">Rôles</h1>
        <Btn onClick={create} busy={busy && !role} disabled={!me.perms?.manage_roles} testId="console-role-create"><Plus className="h-4 w-4" />Créer un rôle</Btn>
      </div>
      {!me.perms?.manage_roles && <NoPerm>Le bot n'a pas la permission « Gérer les rôles » ici.</NoPerm>}
      <p className="mb-3 text-sm text-white/50">Le bot ne peut modifier que les rôles placés sous son propre rôle le plus haut.</p>
      <div className="grid gap-4 md:grid-cols-[220px_1fr]">
        <ul className="space-y-0.5" data-testid="console-roles">
          {roles.map((r) => (
            <li key={r.id}>
              <button type="button" onClick={() => setSel(r.id)} data-testid={`console-role-${r.id}`}
                className={`flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm ${sel === r.id ? "bg-white/10" : "hover:bg-white/5"}`}>
                <span className="h-3 w-3 shrink-0 rounded-full" style={{ background: r.color || "#99aab5" }} />
                <span className="truncate">{r.id === guild.id ? "@everyone" : r.name}</span>
                {(r.managed || !editable(r)) && <Shield className="ml-auto h-3.5 w-3.5 shrink-0 text-white/30" title="Non modifiable par le bot" />}
              </button>
            </li>
          ))}
        </ul>
        {role && draft ? (
          <div className="rounded-lg bg-[#2b2d31] p-4">
            {!editable(role) && <NoPerm>{role.managed ? "Ce rôle est géré par une intégration (bot, boost) : Discord ne le laisse pas modifier." : role.id === guild.id ? "@everyone ne peut pas être renommé ni supprimé." : "Ce rôle est au-dessus du rôle du bot (ou le bot n'a pas « Gérer les rôles »)."}</NoPerm>}
            <Label>Nom du rôle</Label>
            <input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} maxLength={100} disabled={!editable(role)} className={inputCls} data-testid="console-role-name" />
            <Label>Couleur</Label>
            <div className="flex items-center gap-2">
              <input type="color" value={draft.color || "#99aab5"} onChange={(e) => setDraft({ ...draft, color: e.target.value })} disabled={!editable(role)}
                className="h-10 w-14 cursor-pointer rounded border-0 bg-transparent" data-testid="console-role-color" />
              {["#1abc9c", "#2ecc71", "#3498db", "#9b59b6", "#e91e63", "#f1c40f", "#e67e22", "#e74c3c"].map((c) => (
                <button key={c} type="button" disabled={!editable(role)} onClick={() => setDraft({ ...draft, color: c })} className="h-7 w-7 rounded" style={{ background: c }} aria-label={c} />
              ))}
              <button type="button" disabled={!editable(role)} onClick={() => setDraft({ ...draft, color: "" })} className="text-xs text-white/60 hover:underline">Sans couleur</button>
            </div>
            {[["hoist", "Afficher les membres de ce rôle séparément"], ["mentionable", "Permettre à tout le monde de @mentionner ce rôle"]].map(([k, label]) => (
              <label key={k} className="mt-4 flex cursor-pointer items-center justify-between gap-3 text-sm">
                <span>{label}</span>
                <input type="checkbox" checked={!!draft[k]} disabled={!editable(role)} onChange={(e) => setDraft({ ...draft, [k]: e.target.checked })} className="h-5 w-5 accent-[#5865f2]" />
              </label>
            ))}
            {editable(role) && (
              <div className="mt-6 flex items-center gap-2">
                <Btn kind="danger" onClick={remove} busy={busy} testId="console-role-delete"><Trash2 className="h-4 w-4" />Supprimer</Btn>
                <span className="flex-1" />
                <Btn onClick={save} busy={busy} testId="console-role-save">Enregistrer</Btn>
              </div>
            )}
          </div>
        ) : <div className="rounded-lg bg-[#2b2d31] p-6 text-sm text-white/50">Choisis un rôle pour le modifier.</div>}
      </div>
    </>
  );
}

function Bans({ api, botId, guild, canBan }) {
  const [bans, setBans] = useState(null);
  const load = () => api.get(`/${botId}/console/guilds/${guild.id}/bans`).then((r) => setBans(r.bans)).catch((err) => { setBans([]); toast.error(errorText(err, "Bannissements illisibles")); });
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const unban = async (b) => {
    try { await api.del(`/${botId}/console/guilds/${guild.id}/bans/${b.id}`); toast.success(`${b.name} n'est plus banni`); load(); } catch (err) { toast.error(errorText(err, "Impossible")); }
  };
  return (
    <>
      <h1 className="mb-5 text-xl font-semibold">Bannissements</h1>
      {!canBan && <NoPerm>Le bot n'a pas la permission « Bannir des membres » ici.</NoPerm>}
      {!bans ? <p className="text-sm text-white/50">Chargement…</p> : bans.length === 0 ? <p className="text-sm text-white/50">Personne n'est banni de ce serveur.</p> : (
        <ul className="divide-y divide-white/5" data-testid="console-bans">
          {bans.map((b) => (
            <li key={b.id} className="flex items-center gap-3 py-2.5">
              <img src={b.avatar_url} alt="" className="h-9 w-9 rounded-full" />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium">{b.name} <span className="text-white/40">{b.username}</span></div>
                <div className="truncate text-xs text-white/50">{b.reason || "Aucune raison"}</div>
              </div>
              <Btn kind="ghost" onClick={() => unban(b)}>Révoquer</Btn>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

// ── Invitations du serveur ──

export const INVITE_AGES = [[1800, "30 minutes"], [3600, "1 heure"], [21600, "6 heures"], [43200, "12 heures"], [86400, "1 jour"], [604800, "7 jours"], [0, "Jamais"]];
export const INVITE_USES = [[0, "Pas de limite"], [1, "1 utilisation"], [5, "5 utilisations"], [10, "10 utilisations"], [25, "25 utilisations"], [50, "50 utilisations"], [100, "100 utilisations"]];
const ageText = (s) => (INVITE_AGES.find(([v]) => v === s) || [0, ""])[1];

const copyText = (text, done) => {
  if (navigator.clipboard?.writeText) navigator.clipboard.writeText(text).then(done, () => toast.error("Copie impossible"));
  else toast.error("Copie impossible");
};

function expiresIn(invite) {
  if (!invite.expires_at) return "Jamais";
  const ms = new Date(invite.expires_at).getTime() - Date.now();
  if (!(ms > 0)) return "Expirée";
  const h = Math.floor(ms / 3600000);
  if (h >= 24) return `${Math.floor(h / 24)} j ${h % 24} h`;
  if (h >= 1) return `${h} h ${Math.floor((ms % 3600000) / 60000)} min`;
  return `${Math.max(1, Math.floor(ms / 60000))} min`;
}

function Invites({ api, botId, guild, canManage }) {
  const [invites, setInvites] = useState(null);
  const load = () => api.get(`/${botId}/console/guilds/${guild.id}/invites`).then((r) => setInvites(r.invites))
    .catch((err) => { setInvites([]); toast.error(errorText(err, "Invitations illisibles")); });
  useEffect(() => { if (canManage) load(); else setInvites([]); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const revoke = async (i) => {
    try { await api.del(`/${botId}/console/guilds/${guild.id}/invites/${i.code}`); toast.success("Invitation révoquée"); load(); } catch (err) { toast.error(errorText(err, "Impossible")); }
  };
  return (
    <>
      <h1 className="mb-1 text-xl font-semibold">Invitations</h1>
      <p className="mb-5 text-sm text-white/60">Les liens d'invitation en cours sur ce serveur. Une invitation révoquée ne marche plus.</p>
      {!canManage && <NoPerm>Le bot n'a pas la permission « Gérer le serveur » : il ne peut pas lister ni révoquer les invitations.</NoPerm>}
      {!invites ? <p className="text-sm text-white/50">Chargement…</p> : invites.length === 0 ? <p className="text-sm text-white/50">{canManage ? "Aucune invitation en cours." : ""}</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm" data-testid="console-invites">
            <thead className="text-xs uppercase text-white/50">
              <tr><th className="py-2 pr-3 font-semibold">Lien</th><th className="py-2 pr-3 font-semibold">Créé par</th><th className="py-2 pr-3 font-semibold">Utilisations</th><th className="py-2 pr-3 font-semibold">Expire dans</th><th /></tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {invites.map((i) => (
                <tr key={i.code}>
                  <td className="py-2.5 pr-3"><div className="font-mono text-[13px]">discord.gg/{i.code}</div><div className="text-xs text-white/50">#{i.channel.name}</div></td>
                  <td className="py-2.5 pr-3 text-white/70">{i.inviter || "—"}</td>
                  <td className="py-2.5 pr-3 text-white/70">{i.uses}{i.max_uses ? ` / ${i.max_uses}` : ""}</td>
                  <td className="py-2.5 pr-3 text-white/70">{expiresIn(i)}</td>
                  <td className="py-2.5 text-right whitespace-nowrap">
                    <button type="button" onClick={() => copyText(i.url, () => toast.success("Lien copié"))} className="mr-1 rounded p-1.5 text-white/60 hover:bg-white/10 hover:text-white" title="Copier le lien"><Copy className="h-4 w-4" /></button>
                    <button type="button" onClick={() => revoke(i)} className="rounded p-1.5 text-[#f23f43] hover:bg-[#da373c]/20" title="Révoquer" data-testid={`console-invite-revoke-${i.code}`}><Trash2 className="h-4 w-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// « Inviter des amis » : un lien du serveur à copier, comme sur Discord.
export function InviteModal({ api, botId, guild, channels, channelId = "", canInvite = true, onClose }) {
  const texts = (channels || []).filter((c) => c.kind !== "category");
  const [channel, setChannel] = useState(channelId || (texts.find((c) => c.writable) || texts[0] || {}).id || "");
  const [maxAge, setMaxAge] = useState(604800);
  const [maxUses, setMaxUses] = useState(0);
  const [temporary, setTemporary] = useState(false);
  const [editing, setEditing] = useState(false);
  const [invite, setInvite] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const create = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/${botId}/console/guilds/${guild.id}/invites`, { channel_id: channel, max_age: maxAge, max_uses: maxUses, temporary });
      setInvite(r.invite);
      setEditing(false);
      setCopied(false);
    } catch (err) { toast.error(errorText(err, "Impossible de créer l'invitation")); }
    setBusy(false);
  };
  useEffect(() => { if (canInvite) create(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const copy = () => { if (invite) copyText(invite.url, () => { setCopied(true); toast.success("Lien d'invitation copié"); setTimeout(() => setCopied(false), 2000); }); };
  const current = texts.find((c) => c.id === (invite?.channel?.id || channel));
  return (
    <Modal title={`Inviter des amis sur ${guild.name}`} subtitle={current ? `Les personnes invitées arriveront dans #${current.name}` : ""} onClose={onClose} testId="console-invite">
      {!canInvite && <NoPerm>Le bot n'a pas la permission « Créer une invitation » sur ce serveur.</NoPerm>}
      <Label>Envoie ce lien d'invitation à un ami</Label>
      <div className="flex items-center gap-2 rounded bg-[#1e1f22] p-1 pl-3">
        <input readOnly value={invite ? invite.url : busy ? "Création du lien…" : ""} className="min-w-0 flex-1 bg-transparent py-1.5 font-mono text-[15px] text-white focus:outline-none"
          onFocus={(e) => e.target.select()} data-testid="console-invite-url" />
        <button type="button" onClick={copy} disabled={!invite} data-testid="console-invite-copy"
          className={`inline-flex min-w-[86px] items-center justify-center gap-1.5 rounded px-4 py-2 text-sm font-medium text-white transition disabled:opacity-50 ${copied ? "bg-[#248046]" : "bg-[#5865f2] hover:bg-[#4752c4]"}`}>
          {copied ? <><Check className="h-4 w-4" />Copié</> : <><Copy className="h-4 w-4" />Copier</>}
        </button>
      </div>
      {invite && !editing && (
        <p className="mt-2 text-xs text-white/60">
          {invite.max_age ? `Ton lien d'invitation expire dans ${ageText(invite.max_age)}` : "Ton lien d'invitation n'expire jamais"}
          {invite.max_uses ? ` et peut servir ${invite.max_uses} fois` : ""}.{" "}
          <button type="button" onClick={() => setEditing(true)} className="text-[#00a8fc] hover:underline" data-testid="console-invite-edit">Modifier le lien d'invitation.</button>
        </p>
      )}
      {(editing || (!invite && !busy && canInvite)) && (
        <div className="mt-3 rounded bg-[#2b2d31] p-3" data-testid="console-invite-settings">
          <Label>Salon d'arrivée</Label>
          <select value={channel} onChange={(e) => setChannel(e.target.value)} className={inputCls} data-testid="console-invite-channel">
            {texts.map((c) => <option key={c.id} value={c.id}>{c.kind === "voice" ? "🔊 " : "# "}{c.name}</option>)}
          </select>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Expire après</Label>
              <select value={maxAge} onChange={(e) => setMaxAge(Number(e.target.value))} className={inputCls} data-testid="console-invite-age">
                {INVITE_AGES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <Label>Nombre max d'utilisations</Label>
              <select value={maxUses} onChange={(e) => setMaxUses(Number(e.target.value))} className={inputCls} data-testid="console-invite-uses">
                {INVITE_USES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
          </div>
          <label className="mt-4 flex cursor-pointer items-start gap-3 text-sm">
            <input type="checkbox" checked={temporary} onChange={(e) => setTemporary(e.target.checked)} className="mt-0.5 h-4 w-4 accent-[#5865f2]" />
            <span>Accorder un statut de membre temporaire<span className="block text-xs text-white/50">Les membres temporaires sont expulsés quand ils se déconnectent, sauf si on leur donne un rôle.</span></span>
          </label>
          <div className="mt-4 flex justify-end gap-2">
            {invite && <Btn kind="ghost" onClick={() => setEditing(false)}>Annuler</Btn>}
            <Btn onClick={create} busy={busy} disabled={!canInvite || !channel} testId="console-invite-generate">Générer un nouveau lien</Btn>
          </div>
        </div>
      )}
    </Modal>
  );
}

// ── Profil du bot ──

export function ProfileModal({ api, botId, bot, guild, nick, onClose, onSaved }) {
  const [username, setUsername] = useState(bot.user.username || "");
  const [avatar, setAvatar] = useState(null);
  const [banner, setBanner] = useState(null);
  const [serverNick, setServerNick] = useState(nick || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const body = {};
      if (username.trim() && username.trim() !== bot.user.username) body.username = username.trim();
      if (avatar !== null) body.avatar = avatar;
      if (banner !== null) body.banner = banner;
      if (Object.keys(body).length) await api.patch(`/${botId}/console/profile`, body);
      if (guild && serverNick.trim() !== (nick || "")) await api.patch(`/${botId}/console/guilds/${guild.id}/me`, { nick: serverNick.trim() });
      toast.success("Profil du bot enregistré");
      onSaved?.();
      onClose();
    } catch (err) { toast.error(errorText(err, "Modification impossible")); }
    setBusy(false);
  };
  return (
    <Modal title="Profil du bot" subtitle="Le nom, l'avatar et la bannière changent sur tout Discord ; le pseudo seulement sur ce serveur." onClose={onClose} testId="console-profile"
      footer={<><Btn kind="ghost" onClick={onClose}>Annuler</Btn><Btn onClick={save} busy={busy} testId="console-profile-save">Enregistrer</Btn></>}>
      <Label>Avatar</Label>
      <ImagePick value={avatar} current={bot.user.avatar_url} onChange={setAvatar} label="Changer l'avatar" testId="console-profile-avatar" />
      <Label>Bannière</Label>
      <ImagePick value={banner} current="" onChange={setBanner} wide label="Changer la bannière" />
      <Label>Nom d'utilisateur</Label>
      <input value={username} onChange={(e) => setUsername(e.target.value.replace(/[^\w.]/g, "").toLowerCase())} maxLength={32} className={inputCls} data-testid="console-profile-username" />
      <p className="mt-1 text-[11px] text-white/40">Discord limite à 2 changements de nom par heure.</p>
      {guild && (
        <>
          <Label>Pseudo sur {guild.name}</Label>
          <input value={serverNick} onChange={(e) => setServerNick(e.target.value)} maxLength={32} placeholder={bot.user.name} className={inputCls} data-testid="console-profile-nick" />
        </>
      )}
    </Modal>
  );
}

// ── Salons ──

const KINDS = [["text", "Texte", Hash, "Envoie des messages, des images, des GIF…"], ["voice", "Vocal", Volume2, "Discute en vocal ou en vidéo"],
  ["announcement", "Annonces", Megaphone, "Messages que d'autres serveurs peuvent suivre"], ["category", "Catégorie", Folder, "Range des salons ensemble"]];

export function ChannelModal({ api, botId, guildId, channel, categories, parentId = "", kind: startKind = "text", onClose, onDone }) {
  const editing = !!channel;
  const [kind, setKind] = useState(editing ? channel.kind : startKind);
  const [name, setName] = useState(editing ? channel.name : "");
  const [topic, setTopic] = useState(editing ? channel.topic || "" : "");
  const [parent, setParent] = useState(editing ? channel.parent_id || "" : parentId);
  const [nsfw, setNsfw] = useState(editing ? !!channel.nsfw : false);
  const [slowmode, setSlowmode] = useState(editing ? channel.slowmode || 0 : 0);
  const [busy, setBusy] = useState(false);
  const textual = ["text", "announcement", "forum", "media"].includes(kind);
  const submit = async (e) => {
    e?.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try {
      if (editing) {
        const body = { name };
        if (textual) Object.assign(body, { topic, nsfw }, kind === "text" ? { slowmode } : {});
        await api.patch(`/${botId}/console/channels/${channel.id}`, body);
        toast.success("Salon enregistré");
      } else {
        await api.post(`/${botId}/console/guilds/${guildId}/channels`, { name, kind, parent_id: kind === "category" ? "" : parent, topic: textual ? topic : "" });
        toast.success(kind === "category" ? "Catégorie créée" : "Salon créé");
      }
      onDone?.();
      onClose();
    } catch (err) { toast.error(errorText(err, editing ? "Modification impossible" : "Création impossible")); }
    setBusy(false);
  };
  const title = editing ? (kind === "category" ? "Modifier la catégorie" : "Modifier le salon") : (kind === "category" ? "Créer une catégorie" : "Créer un salon");
  return (
    <Modal title={title} subtitle={!editing && kind !== "category" && parent ? `dans ${categories.find((c) => c.id === parent)?.name || "la catégorie"}` : undefined}
      onClose={onClose} testId="console-channel-modal"
      footer={<><Btn kind="ghost" onClick={onClose}>Annuler</Btn><Btn onClick={submit} busy={busy} disabled={!name.trim()} testId="console-channel-save">{editing ? "Enregistrer" : "Créer"}</Btn></>}>
      <form onSubmit={submit}>
        {!editing && (
          <>
            <Label>Type</Label>
            <div className="space-y-1.5">
              {KINDS.filter(([k]) => startKind === "category" ? k === "category" : k !== "category").map(([k, label, Icon, desc]) => (
                <label key={k} className={`flex cursor-pointer items-center gap-3 rounded px-3 py-2.5 ${kind === k ? "bg-white/10" : "bg-[#2b2d31] hover:bg-white/5"}`}>
                  <Icon className="h-5 w-5 text-white/60" />
                  <span className="flex-1"><span className="block text-sm font-medium">{label}</span><span className="block text-xs text-white/50">{desc}</span></span>
                  <input type="radio" name="kind" checked={kind === k} onChange={() => setKind(k)} className="h-4 w-4 accent-white" />
                </label>
              ))}
            </div>
          </>
        )}
        <Label>{kind === "category" ? "Nom de la catégorie" : "Nom du salon"}</Label>
        <div className="relative">
          {kind !== "category" && <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-white/50">{kind === "voice" ? <Volume2 className="h-4 w-4" /> : <Hash className="h-4 w-4" />}</span>}
          <input autoFocus value={name} onChange={(e) => setName(["text", "announcement"].includes(kind) ? e.target.value.toLowerCase().replace(/\s/g, "-") : e.target.value)}
            maxLength={100} placeholder={kind === "category" ? "Nouvelle catégorie" : "nouveau-salon"} className={`${inputCls} ${kind !== "category" ? "pl-9" : ""}`} data-testid="console-channel-name" />
        </div>
        {!editing && kind !== "category" && categories.length > 0 && (
          <>
            <Label>Catégorie</Label>
            <select value={parent} onChange={(e) => setParent(e.target.value)} className={inputCls}>
              <option value="">Aucune</option>
              {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </>
        )}
        {textual && (
          <>
            <Label>Sujet du salon</Label>
            <textarea value={topic} onChange={(e) => setTopic(e.target.value)} maxLength={1024} rows={2} placeholder="Dis à tout le monde de quoi parle ce salon" className={`${inputCls} resize-none`} data-testid="console-channel-topic" />
          </>
        )}
        {editing && kind === "text" && (
          <>
            <Label>Mode lent</Label>
            <select value={slowmode} onChange={(e) => setSlowmode(Number(e.target.value))} className={inputCls}>
              {[[0, "Désactivé"], [5, "5 s"], [10, "10 s"], [30, "30 s"], [60, "1 min"], [300, "5 min"], [900, "15 min"], [3600, "1 h"], [21600, "6 h"]].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </>
        )}
        {editing && textual && (
          <label className="mt-4 flex cursor-pointer items-center justify-between text-sm">
            <span>Salon soumis à une limite d'âge (NSFW)</span>
            <input type="checkbox" checked={nsfw} onChange={(e) => setNsfw(e.target.checked)} className="h-5 w-5 accent-[#5865f2]" />
          </label>
        )}
      </form>
    </Modal>
  );
}

// ── Membres : pseudo, expulsion, bannissement ──

export function NickModal({ api, botId, guildId, member, onClose, onDone }) {
  const [nick, setNick] = useState(member.nick || "");
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      await api.patch(`/${botId}/console/guilds/${guildId}/members/${member.id}`, { nick });
      toast.success(nick.trim() ? "Pseudo changé" : "Pseudo retiré");
      onDone?.();
      onClose();
    } catch (err) { toast.error(errorText(err, "Impossible de changer le pseudo")); }
    setBusy(false);
  };
  return (
    <Modal title="Changer le pseudo" subtitle={`Pseudo de ${member.name} sur ce serveur.`} onClose={onClose} testId="console-nick"
      footer={<><Btn kind="ghost" onClick={() => setNick("")}>Réinitialiser</Btn><Btn onClick={save} busy={busy} testId="console-nick-save">Enregistrer</Btn></>}>
      <form onSubmit={save}>
        <Label>Pseudo</Label>
        <input autoFocus value={nick} onChange={(e) => setNick(e.target.value)} maxLength={32} placeholder={member.name} className={inputCls} data-testid="console-nick-input" />
      </form>
    </Modal>
  );
}

export function SanctionModal({ api, botId, guildId, member, kind, onClose, onDone }) {
  const ban = kind === "ban";
  const [reason, setReason] = useState("");
  const [hours, setHours] = useState(0);
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      await api.post(`/${botId}/console/guilds/${guildId}/members/${member.id}/${kind}`, { reason, delete_hours: hours });
      toast.success(ban ? `${member.name} a été banni` : `${member.name} a été expulsé`);
      onDone?.();
      onClose();
    } catch (err) { toast.error(errorText(err, ban ? "Bannissement impossible" : "Expulsion impossible")); }
    setBusy(false);
  };
  return (
    <Modal title={`${ban ? "Bannir" : "Expulser"} ${member.nick || member.name} ?`} onClose={onClose} testId="console-sanction"
      subtitle={ban ? "Il ne pourra plus revenir sur le serveur tant qu'il est banni." : "Il pourra revenir avec une nouvelle invitation."}
      footer={<><Btn kind="ghost" onClick={onClose}>Annuler</Btn><Btn kind="danger" onClick={go} busy={busy} testId="console-sanction-go">{ban ? <Ban className="h-4 w-4" /> : null}{ban ? "Bannir" : "Expulser"}</Btn></>}>
      {ban && (
        <>
          <Label>Supprimer l'historique des messages</Label>
          <select value={hours} onChange={(e) => setHours(Number(e.target.value))} className={inputCls} data-testid="console-ban-history">
            {[[0, "Ne rien supprimer"], [1, "Dernière heure"], [6, "6 dernières heures"], [12, "12 dernières heures"], [24, "24 dernières heures"], [72, "3 derniers jours"], [168, "7 derniers jours"]].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </>
      )}
      <Label>Raison {ban ? "du bannissement" : "de l'expulsion"}</Label>
      <textarea value={reason} onChange={(e) => setReason(e.target.value)} maxLength={400} rows={3} placeholder="Visible dans le journal des actions du serveur" className={`${inputCls} resize-none`} data-testid="console-sanction-reason" />
    </Modal>
  );
}

export function ConfirmModal({ title, text, confirm, danger, onConfirm, onClose }) {
  const [busy, setBusy] = useState(false);
  return (
    <Modal title={title} onClose={onClose} testId="console-confirm"
      footer={<><Btn kind="ghost" onClick={onClose}>Annuler</Btn><Btn kind={danger ? "danger" : "primary"} busy={busy} testId="console-confirm-go"
        onClick={async () => { setBusy(true); const ok = await onConfirm(); setBusy(false); if (ok !== false) onClose(); }}>{confirm}</Btn></>}>
      <p className="text-sm text-white/80">{text}</p>
    </Modal>
  );
}

// « Ajouter le bot à un serveur » : Discord ne laisse pas un bot rejoindre avec
// un lien discord.gg, il faut sa page d'autorisation. Avec le lien du serveur,
// le serveur est retrouvé et déjà choisi sur cette page.
export function AddBotModal({ api, botId, botName, onClose, onRefresh }) {
  const [link, setLink] = useState("");
  const [found, setFound] = useState(null);
  const [busy, setBusy] = useState(false);
  const [opened, setOpened] = useState(false);
  const open = (url) => { window.open(url, "_blank", "noopener,noreferrer"); setOpened(true); };
  const check = async (e) => {
    e?.preventDefault?.();
    if (!link.trim()) return;
    setBusy(true);
    try { setFound(await api.post(`/${botId}/console/add-link`, { invite: link.trim() })); } catch (err) { setFound(null); toast.error(errorText(err, "Lien du serveur introuvable")); }
    setBusy(false);
  };
  const anyServer = async () => {
    setBusy(true);
    try { open((await api.post(`/${botId}/console/add-link`, {})).url); } catch (err) { toast.error(errorText(err, "Impossible d'ouvrir Discord")); }
    setBusy(false);
  };
  const g = found?.guild;
  return (
    <Modal title={`Ajouter ${botName || "le bot"} à un serveur`} subtitle="Colle le lien d'invitation du serveur : Discord ouvrira la page pour y ajouter le bot, avec ce serveur déjà choisi." onClose={onClose} testId="console-addbot">
      <form onSubmit={check}>
        <Label>Lien du serveur</Label>
        <div className="flex gap-2">
          <input value={link} onChange={(e) => { setLink(e.target.value); setFound(null); }} placeholder="https://discord.gg/abc123" className={inputCls} autoFocus data-testid="console-addbot-link" />
          <Btn type="submit" busy={busy} disabled={!link.trim()} testId="console-addbot-check">Trouver</Btn>
        </div>
      </form>
      {g && (
        <div className="mt-4 flex items-center gap-3 rounded-lg bg-[#2b2d31] p-3" data-testid="console-addbot-guild">
          {g.icon_url ? <img src={g.icon_url} alt="" className="h-12 w-12 rounded-2xl object-cover" />
            : <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#5865f2] font-semibold">{g.name.split(/\s+/).map((w) => w[0]).join("").slice(0, 3)}</span>}
          <div className="min-w-0 flex-1">
            <div className="truncate font-semibold">{g.name}</div>
            <div className="text-xs text-white/60">{g.already ? "Le bot est déjà sur ce serveur" : g.members ? `${g.members.toLocaleString("fr-FR")} membres` : "Serveur trouvé"}</div>
          </div>
          <Btn onClick={() => open(found.url)} testId="console-addbot-open"><ExternalLink className="h-4 w-4" />{g.already ? "Rouvrir" : "Ajouter le bot"}</Btn>
        </div>
      )}
      <p className="mt-3 text-xs text-white/50">Il faut avoir la permission « Gérer le serveur » sur ce serveur pour y ajouter un bot.</p>
      <div className="mt-4 flex items-center gap-2 border-t border-white/10 pt-3 text-sm">
        <Link2 className="h-4 w-4 text-white/40" />
        <span className="flex-1 text-white/60">Pas de lien ?</span>
        <button type="button" onClick={anyServer} disabled={busy} className="text-[#00a8fc] hover:underline disabled:opacity-50" data-testid="console-addbot-any">Choisir le serveur sur Discord</button>
      </div>
      {opened && (
        <div className="mt-3 flex items-center gap-2 rounded bg-[#248046]/15 p-2.5 text-sm text-[#3ba55d]">
          <Check className="h-4 w-4" /><span className="flex-1">Une fois le bot ajouté sur Discord, actualise la liste des serveurs.</span>
          <button type="button" onClick={() => { onRefresh?.(); onClose(); }} className="font-medium hover:underline" data-testid="console-addbot-refresh">Actualiser</button>
        </div>
      )}
    </Modal>
  );
}
